// Action creators
import { FilterActions_fetchFilterFromUrlAndApi } from 'app/shared/flux/actions/FilterActions';
import AreaActions from 'app/shared/flux/actions/AreaActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';

// Misc / Utils
import CONSTANTS from 'app/shared/constants/ValueConstants';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';

// Types
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';

// Type Definitions
interface RouteState {
  pathname: string;
  query: Record<string, string | number | boolean | undefined>;
  path?: string;
  search?: Record<string, string | number | boolean | undefined>;
}

interface Trace {
  finishAsyncSpan?: (params: { marker: string }) => void;
  startAsyncSpan?: (params: { marker: string }) => void;
  startSyncSpan?: (params: { marker: string }) => void;
}

interface ReduxStore {
  getState: () => RootReduxState;
  dispatch: ThunkDispatch<RootReduxState, undefined, AnyAction>;
}

interface ReactContext {
  state: RouteState;
  store: ReduxStore;
  trace?: Trace;
  ip?: string;
}

export interface ServerRouteResponse {
  error?: boolean;
  changeHttpStatus?: number;
  redirect?: boolean;
  to?: string;
  success?: boolean;
}

interface FetchAreaParams {
  areaResourceId: string;
}

const logger = getGlobalLogger('template/searchresultspage/serverroutewillmount');

export const srwmFetchFilterFromUrlAndApi = (reactContext: ReactContext): Promise<ServerRouteResponse> => {
  const { state, store } = reactContext;
  const { pathname, query } = state;
  const currState: RootReduxState = store.getState();
  const resourceId = currState?.area?.area?.resourceId;

  return store
    .dispatch(FilterActions_fetchFilterFromUrlAndApi(pathname, query))
    .then((res: ServerRouteResponse) => {
      if (res.error) {
        return {
          changeHttpStatus: 404,
        };
      }
      return res;
    })
    .catch((err: Error) => {
      logError({
        error: err,
        context: {
          message: 'error in SearchResultsPage#srwmFetchFilterFromUrlAndApi',
          pathname,
          resourceId,
        },
      });
      return {
        error: true,
        changeHttpStatus: 500,
      };
    });
};

export const srwmFetchAreaDataByResourceId = (
  reactContext: ReactContext,
  { areaResourceId: resourceId }: FetchAreaParams,
): Promise<ServerRouteResponse> => {
  const { state, store } = reactContext;
  const { pathname } = state;

  return store
    .dispatch(AreaActions.fetchAreaDataByResourceId(resourceId))
    .then((res: ServerRouteResponse) => {
      if (res.error) {
        return {
          changeHttpStatus: 404,
        };
      }

      return res;
    })
    .catch((err: Error) => {
      logError({
        error: err,
        context: {
          message: 'error in SearchResultsPage#srwmFetchAreaDataByResourceId',
          pathname,
          resourceId,
        },
      });
      return {
        error: true,
        changeHttpStatus: 500,
      };
    });
};

export const srwmFetchListingsAndOtherData = (reactContext: ReactContext): Array<Promise<unknown>> => {
  const { store } = reactContext;
  const { query, pathname } = reactContext.state;
  const promises: Array<Promise<unknown>> = [];
  const currState: RootReduxState = store.getState();
  const page: number = query.page ? Math.max(Number(query.page), 0) : 0;
  //HPWEB-5917 - state pages sorted by newest
  const areaType = currState?.area?.area?.type;
  if (areaType === 'state') {
    currState.filter.orderBy = 'activated';
  }
  //end HPWEB-5917
  const byCoordsAndHistogramParams = {
    filter: currState.filter,
    page,
  };

  const resourceId: string | undefined = currState.area.area.resourceId;
  const searchSlug: string = currState.filter.search.slug;
  const seoFooterLinksParams = {
    resourceId,
    searchSlug,
  };

  // Guard: Only fetch area-specific data if resourceId is defined
  if (!resourceId) {
    logger?.error('Missing resourceId when fetching area-specific data in srwmFetchListingsAndOtherData', {
      pathname,
      resourceId,
      searchSlug,
      hasArea: !!currState.area,
      hasAreaArea: !!currState.area?.area,
    });
  }

  // Promises will be called in parallel
  promises.push(
    // Listings
    store.dispatch(ListingEngineActions.fetchListingsOnlyByCoords(byCoordsAndHistogramParams, undefined)),
    // Area histogram data (not to be confused w/ price filter histogram data)
    store.dispatch(AreaActions.fetchPriceHistogramData(byCoordsAndHistogramParams)),
  );

  // Only fetch area-specific data if resourceId is defined to prevent /undefined/ URLs
  if (resourceId) {
    promises.push(
      // SEO footer links
      store.dispatch(AreaActions.loadAreaSeoLinks(seoFooterLinksParams)),
      // Area articles (requred for all slugs for SEO reasons)
      store.dispatch(AreaActions.loadArticles(resourceId)),
      store.dispatch(AreaActions.loadFacts(resourceId)),
      store.dispatch(AreaActions.loadFactsByBeds(resourceId)),
    );
  }

  return promises;
};

export const srwmCheckAndFetchNearbyListings = (reactContext: ReactContext): Promise<ServerRouteResponse> => {
  const { store } = reactContext;
  const latestState: RootReduxState = store.getState();
  const maxBuildings: number = latestState.listings.totalBuildings;
  const maxPage: number = Math.max(Math.ceil(maxBuildings / CONSTANTS.MAX_LISTINGS_PER_PAGE), 1);
  const currentPage: number | string | boolean | undefined =
    latestState.location.current && latestState.location.current.query.page;
  const onLastPage: boolean = maxPage === Number(currentPage) || (maxPage === 1 && !currentPage);

  if (onLastPage) {
    const areaResourceId: string = latestState.area.area.resourceId;
    return store.dispatch(ListingEngineActions.fetchNearbyAreasListingsForMobileSRP(areaResourceId));
  }

  return Promise.resolve({ success: true });
};
